import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buildHomeNavigationTarget, resolveActiveSiteCode } from '../utils/navigationContext';

interface AdminRouteProps {
  children: React.ReactNode;
}

/**
 * AdminRoute component that ensures only admin users can access admin pages
 * Redirects to navigation page if user is not admin
 */
const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, user, isGlobalAdmin } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-[#F2F8FC]/30 dark:from-[#0B1120] dark:to-[#0F172A]">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#0084CF]/30 border-t-[#0084CF] rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-slate-300 font-medium">Checking admin access...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user is admin - Updated to include new role system
  const roleName = String((user as any)?.roleName || user?.role || '').toLowerCase();
  const isAdmin =
    user.role === 'admin' ||
    isGlobalAdmin ||
    (user as any)?.is_superuser ||
    (user as any)?.is_super_admin ||
    ['system_admin', 'super_admin', 'ho_admin', 'company_admin', 'site_admin'].includes(roleName);

  // If user is not admin, redirect to navigation page
  if (!isAdmin) {
    const siteCode = resolveActiveSiteCode({
      userSiteCode: user?.siteCode,
      userTenant: user?.tenant,
      preferStoredContext: true
    });
    const target = buildHomeNavigationTarget({
      user,
      isGlobalAdmin,
      siteCode
    });
    return <Navigate to={target} replace />;
  }

  // User is authenticated and is admin, render the admin content
  return <>{children}</>;
};

export default AdminRoute;

